// SVG path -> polylines, for line drawing (stroke grows along the arc length) and morphing (point-wise interpolation).
// Supported commands: M L H V C S Q T A Z, absolute and relative. Curves are flattened; arcs are flattened too.
export interface Poly { pts: number[]; closed: boolean }   // pts = [x0,y0,x1,y1,...]

const TOKEN = /([MmLlHhVvCcSsQqTtAaZz])|([-+]?(?:\d*\.\d+|\d+\.?)(?:[eE][-+]?\d+)?)/g;
const ARGS: Record<string, number> = { m: 2, l: 2, h: 1, v: 1, c: 6, s: 4, q: 4, t: 2, a: 7, z: 0 };
const STEPS = 20;

export function parsePath(d: string): Poly[] {
  const out: Poly[] = [];
  let cur: Poly | null = null;
  let x = 0, y = 0, sx = 0, sy = 0, cmd = "", lcx = 0, lcy = 0, prev = "";
  const toks: (string | number)[] = [];
  for (const m of d.matchAll(TOKEN)) toks.push(m[1] ?? parseFloat(m[2]!));
  let i = 0;
  const num = (): number => { const v = toks[i++]; return typeof v === "number" ? v : 0; };
  const lineTo = (nx: number, ny: number) => { if (!cur) { cur = { pts: [x, y], closed: false }; out.push(cur); } cur.pts.push(nx, ny); x = nx; y = ny; };
  while (i < toks.length) {
    const t = toks[i];
    if (typeof t === "string") { cmd = t; i++; if (cmd === "Z" || cmd === "z") { if (cur) { cur.closed = true; } x = sx; y = sy; cur = null; prev = "z"; continue; } }
    else if (!cmd) { i++; continue; }
    else if (cmd === "M") cmd = "L"; else if (cmd === "m") cmd = "l";
    const rel = cmd === cmd.toLowerCase(), c = cmd.toLowerCase();
    const ox = rel ? x : 0, oy = rel ? y : 0;
    if (ARGS[c] === undefined) { i++; continue; }
    switch (c) {
      case "m": { x = num() + ox; y = num() + oy; sx = x; sy = y; cur = { pts: [x, y], closed: false }; out.push(cur); break; }
      case "l": lineTo(num() + ox, num() + oy); break;
      case "h": lineTo(num() + ox, y); break;
      case "v": lineTo(x, num() + oy); break;
      case "c": case "s": {
        let x1: number, y1: number;
        if (c === "c") { x1 = num() + ox; y1 = num() + oy; }
        else if (prev === "c" || prev === "s") { x1 = 2 * x - lcx; y1 = 2 * y - lcy; } else { x1 = x; y1 = y; }
        const x2 = num() + ox, y2 = num() + oy, ex = num() + ox, ey = num() + oy, x0 = x, y0 = y;
        for (let k = 1; k <= STEPS; k++) {
          const u = k / STEPS, v = 1 - u;
          lineTo(v * v * v * x0 + 3 * v * v * u * x1 + 3 * v * u * u * x2 + u * u * u * ex,
                 v * v * v * y0 + 3 * v * v * u * y1 + 3 * v * u * u * y2 + u * u * u * ey);
        }
        lcx = x2; lcy = y2; break;
      }
      case "q": case "t": {
        let x1: number, y1: number;
        if (c === "q") { x1 = num() + ox; y1 = num() + oy; }
        else if (prev === "q" || prev === "t") { x1 = 2 * x - lcx; y1 = 2 * y - lcy; } else { x1 = x; y1 = y; }
        const ex = num() + ox, ey = num() + oy, x0 = x, y0 = y;
        for (let k = 1; k <= STEPS; k++) {
          const u = k / STEPS, v = 1 - u;
          lineTo(v * v * x0 + 2 * v * u * x1 + u * u * ex, v * v * y0 + 2 * v * u * y1 + u * u * ey);
        }
        lcx = x1; lcy = y1; break;
      }
      case "a": {
        const rx0 = Math.abs(num()), ry0 = Math.abs(num()), rot = (num() * Math.PI) / 180, large = num() !== 0, sweep = num() !== 0;
        const ex = num() + ox, ey = num() + oy;
        for (const [px, py] of arc(x, y, rx0, ry0, rot, large, sweep, ex, ey)) lineTo(px, py);
        break;
      }
    }
    prev = c;
  }
  return out.filter((p) => p.pts.length >= 4);
}

/** Endpoint-to-centre arc conversion (SVG implementation notes F.6), flattened. */
function arc(x1: number, y1: number, rx: number, ry: number, phi: number, large: boolean, sweep: boolean, x2: number, y2: number): [number, number][] {
  if (rx === 0 || ry === 0 || (x1 === x2 && y1 === y2)) return [[x2, y2]];
  const cp = Math.cos(phi), sp = Math.sin(phi);
  const dx = (x1 - x2) / 2, dy = (y1 - y2) / 2;
  const x1p = cp * dx + sp * dy, y1p = -sp * dx + cp * dy;
  const lam = (x1p * x1p) / (rx * rx) + (y1p * y1p) / (ry * ry);
  if (lam > 1) { const s = Math.sqrt(lam); rx *= s; ry *= s; }
  const num = rx * rx * ry * ry - rx * rx * y1p * y1p - ry * ry * x1p * x1p;
  const den = rx * rx * y1p * y1p + ry * ry * x1p * x1p;
  const co = (large === sweep ? -1 : 1) * Math.sqrt(Math.max(0, num / den));
  const cxp = (co * rx * y1p) / ry, cyp = (-co * ry * x1p) / rx;
  const cx = cp * cxp - sp * cyp + (x1 + x2) / 2, cy = sp * cxp + cp * cyp + (y1 + y2) / 2;
  const ang = (ux: number, uy: number, vx: number, vy: number) => Math.atan2(ux * vy - uy * vx, ux * vx + uy * vy);
  const th1 = ang(1, 0, (x1p - cxp) / rx, (y1p - cyp) / ry);
  let dth = ang((x1p - cxp) / rx, (y1p - cyp) / ry, (-x1p - cxp) / rx, (-y1p - cyp) / ry);
  if (!sweep && dth > 0) dth -= Math.PI * 2; else if (sweep && dth < 0) dth += Math.PI * 2;
  const n = Math.max(6, Math.ceil((Math.abs(dth) / (Math.PI * 2)) * 48));
  const res: [number, number][] = [];
  for (let k = 1; k <= n; k++) {
    const a = th1 + (dth * k) / n, ex = rx * Math.cos(a), ey = ry * Math.sin(a);
    res.push([cp * ex - sp * ey + cx, sp * ex + cp * ey + cy]);
  }
  res[res.length - 1] = [x2, y2];
  return res;
}

export function polyLength(p: Poly): number {
  let L = 0;
  for (let i = 2; i < p.pts.length; i += 2) L += Math.hypot(p.pts[i]! - p.pts[i - 2]!, p.pts[i + 1]! - p.pts[i - 1]!);
  if (p.closed && p.pts.length >= 4) L += Math.hypot(p.pts[0]! - p.pts[p.pts.length - 2]!, p.pts[1]! - p.pts[p.pts.length - 1]!);
  return L;
}

/** Points of a polyline (closing segment included) cut at `frac` of its total length. */
export function truncate(p: Poly, frac: number): number[] {
  const pts = p.closed ? [...p.pts, p.pts[0]!, p.pts[1]!] : p.pts;
  const total = polyLength(p);
  if (frac >= 1) return pts;
  let target = total * Math.max(0, frac);
  const out = [pts[0]!, pts[1]!];
  for (let i = 2; i < pts.length; i += 2) {
    const seg = Math.hypot(pts[i]! - pts[i - 2]!, pts[i + 1]! - pts[i - 1]!);
    if (target >= seg) { out.push(pts[i]!, pts[i + 1]!); target -= seg; continue; }
    const u = seg === 0 ? 0 : target / seg;
    out.push(pts[i - 2]! + (pts[i]! - pts[i - 2]!) * u, pts[i - 1]! + (pts[i + 1]! - pts[i - 1]!) * u);
    break;
  }
  return out;
}

/** `n` points evenly spaced along the polyline (by arc length). */
export function resample(p: Poly, n: number): number[] {
  const pts = p.closed ? [...p.pts, p.pts[0]!, p.pts[1]!] : p.pts;
  const lens = [0];
  for (let i = 2; i < pts.length; i += 2) lens.push(lens[lens.length - 1]! + Math.hypot(pts[i]! - pts[i - 2]!, pts[i + 1]! - pts[i - 1]!));
  const total = lens[lens.length - 1]!;
  const out: number[] = [];
  let j = 1;
  const count = p.closed ? n : n - 1;
  for (let k = 0; k < n; k++) {
    const s = total * (count === 0 ? 0 : (p.closed ? k : k) / count);
    while (j < lens.length - 1 && lens[j]! < s) j++;
    const a = lens[j - 1]!, b = lens[j]!, u = b === a ? 0 : (s - a) / (b - a);
    out.push(pts[(j - 1) * 2]! + (pts[j * 2]! - pts[(j - 1) * 2]!) * u, pts[(j - 1) * 2 + 1]! + (pts[j * 2 + 1]! - pts[(j - 1) * 2 + 1]!) * u);
  }
  return out;
}

/** Interpolate subpath i of `a` towards subpath i of `b` (missing subpaths collapse to the other one's first point). */
export function morph(a: Poly[], b: Poly[], u: number): Poly[] {
  if (u <= 0) return a;
  if (u >= 1) return b;
  const n = Math.max(a.length, b.length), out: Poly[] = [];
  for (let i = 0; i < n; i++) {
    const pa = a[i] ?? { pts: [b[i]!.pts[0]!, b[i]!.pts[1]!, b[i]!.pts[0]!, b[i]!.pts[1]!], closed: b[i]!.closed };
    const pb = b[i] ?? { pts: [pa.pts[0]!, pa.pts[1]!, pa.pts[0]!, pa.pts[1]!], closed: pa.closed };
    const N = 96, ra = resample(pa, N), rb = resample(pb, N), pts: number[] = [];
    for (let k = 0; k < ra.length; k++) pts.push(ra[k]! + (rb[k]! - ra[k]!) * u);
    out.push({ pts, closed: pa.closed && pb.closed });
  }
  return out;
}

export function bounds(polys: Poly[]): { x: number; y: number; w: number; h: number } {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const p of polys) for (let i = 0; i < p.pts.length; i += 2) {
    x0 = Math.min(x0, p.pts[i]!); x1 = Math.max(x1, p.pts[i]!); y0 = Math.min(y0, p.pts[i + 1]!); y1 = Math.max(y1, p.pts[i + 1]!);
  }
  return Number.isFinite(x0) ? { x: x0, y: y0, w: x1 - x0, h: y1 - y0 } : { x: 0, y: 0, w: 0, h: 0 };
}
